"use client";

/**
 * Cross-duplicate check for the compare page: warns ONLY when the
 * compared listings are duplicates OF EACH OTHER (the same unit posted
 * twice, usually by different agents) - the case where the side-by-side
 * comparison is quietly meaningless. External duplicates of each
 * listing are deliberately NOT listed here (the analyse page owns that
 * story); a listing's photos appearing elsewhere says nothing about
 * this comparison.
 *
 * Detection reuses /api/listings/duplicates per compared listing and
 * checks whether any structural or photo match points at ANOTHER
 * compared listing. Renders nothing when the compared items are all
 * distinct - the common case adds zero chrome.
 */
import { useEffect, useState } from "react";
import { API_BASE } from "@/lib/api";
import { canonicalListingKey } from "@/lib/listing-url";
import { useT } from "@/lib/locale-context";
import { colorForIndex } from "./palette";
import type { AnalysisResult } from "@/types";

interface DupPair {
  a: number;                    // 0-based indexes into results
  b: number;
  sharedPhotos: number | null;  // matched photo count, when counted
  photoTotal: number;           // denominator for the photo chip
  structural: boolean;          // attributes matched (same-unit fingerprint)
  photosConfirmed: boolean;     // photos matched (counted or via structural row)
}

export default function ComparePhotoReuse({ results }: { results: AnalysisResult[] }) {
  const t = useT();
  const [pairs, setPairs] = useState<DupPair[]>([]);

  useEffect(() => {
    setPairs([]);
    if (results.length < 2) return;
    const keys = results.map((r) => (r.url ? canonicalListingKey(r.url) : null));
    const ctrl = new AbortController();

    Promise.all(
      results.map(async (r, i): Promise<DupPair[]> => {
        if (!r.photo_urls?.length && !r.url) return [];
        let listingId: string | null = null;
        if (keys[i]) listingId = keys[i]!.slice(keys[i]!.indexOf(":") + 1);
        try {
          const resp = await fetch(`${API_BASE}/listings/duplicates`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            signal: ctrl.signal,
            body: JSON.stringify({
              source: r.source ?? null,
              listing_id: listingId,
              latitude: r.latitude ?? null,
              longitude: r.longitude ?? null,
              area_m2: r.area_m2 ?? null,
              room_count: r.room_count ?? null,
              floor: r.floor ?? null,
              total_floors: r.total_floors ?? null,
              mode: r.analysis_mode === "rent" ? "rent" : "sale",
              photo_urls: r.photo_urls?.slice(0, 12) ?? null,
            }),
          });
          if (!resp.ok) return [];
          const d = await resp.json();
          const total: number = d.photo_query_total || 0;
          const found: DupPair[] = [];
          // Does any match point at ANOTHER compared listing?
          for (let j = 0; j < results.length; j++) {
            if (j === i || !keys[j]) continue;
            const hit = (arr: { url: string }[] | undefined) =>
              (arr || []).find((m) => canonicalListingKey(m.url) === keys[j]);
            const photo = hit(d.photo_matches) as { shared_photos?: number } | undefined;
            const structural = hit(d.duplicates) as { photos_match?: boolean } | undefined;
            if (photo || structural) {
              found.push({
                a: Math.min(i, j),
                b: Math.max(i, j),
                sharedPhotos: photo?.shared_photos ?? null,
                photoTotal: total,
                structural: !!structural,
                photosConfirmed: !!photo || structural?.photos_match === true,
              });
            }
          }
          return found;
        } catch {
          return [];
        }
      }),
    ).then((rows) => {
      if (ctrl.signal.aborted) return;
      // Dedup (i,j) found from both sides; keep the photo-count evidence.
      const best = new Map<string, DupPair>();
      for (const p of rows.flat()) {
        const k = `${p.a}-${p.b}`;
        const cur = best.get(k);
        if (!cur) { best.set(k, p); continue; }
        best.set(k, {
          ...cur,
          sharedPhotos: (p.sharedPhotos ?? 0) > (cur.sharedPhotos ?? 0) ? p.sharedPhotos : cur.sharedPhotos,
          structural: cur.structural || p.structural,
          photosConfirmed: cur.photosConfirmed || p.photosConfirmed,
        });
      }
      setPairs([...best.values()].sort((x, y) => x.a - y.a || x.b - y.b));
    });
    return () => ctrl.abort();
  }, [results]);

  if (pairs.length === 0) return null;

  const chip = (i: number) => {
    const pal = colorForIndex(i);
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-bold ${pal.chipBg} ${pal.chipText}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${pal.dot}`} />
        {t("compare.cards.listing_label").replace("{n}", String(i + 1))}
      </span>
    );
  };

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 sm:p-5">
      <h3 className="text-sm font-bold text-gray-900 mb-1">
        {t("compare.photodups.title")}
      </h3>
      <p className="text-xs text-gray-600 mb-3 leading-relaxed">
        {t("compare.photodups.subtitle")}
      </p>
      <div className="space-y-2">
        {pairs.map((p) => (
          <div key={`${p.a}-${p.b}`} className="flex flex-wrap items-center gap-2">
            {chip(p.a)}
            <span className="text-xs text-gray-500">+</span>
            {chip(p.b)}
            {p.structural && (
              <span className="inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                {t("compare.photodups.pair_struct")}
              </span>
            )}
            {p.photosConfirmed && (
              <span className="inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                {p.sharedPhotos != null
                  ? t("result.photodups.shared")
                      .replace("{n}", String(p.sharedPhotos))
                      .replace("{m}", String(Math.max(p.photoTotal, p.sharedPhotos)))
                  : t("result.duplicates.photos_match")}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
